import { CUSTOMER_FUNDING_RATE_CONFIGURATION_SPACE_PARAMETER_SUPPORT } from '@src/components/AppLayout/Panels/Dealer/DealerSpaceParameters';
import { useSpaceParameterControlledFilter } from '@src/components/AppLayout/SpaceParameters';
import { useRoleAuth } from '@src/hooks';
import { useCustomers } from '@src/hooks/useCustomer';
import { useCustomerMarketAccounts } from '@src/providers';
import {
  ACTION,
  BlotterDensity,
  DEFAULT_MAX_ROWS,
  DEFAULT_PAGINATION_SIZE,
  EntityAdmin,
  FUNDING_RATE_CONFIGURATION,
  useSymbolsFilter,
  type ColumnDef,
  type IFundingRateConfiguration,
  type InputsAndDropdownsDrawerOption,
} from '@talos/kyoko';
import { useMemo } from 'react';

const WS_REQUEST = { name: FUNDING_RATE_CONFIGURATION, tag: 'CustomerFundingRateConfiguration' };

export const CustomerFundingRateConfiguration = () => {
  const { isAuthorized } = useRoleAuth();
  const symbolFilter = useSymbolsFilter();
  // Data hooks for dropdowns
  const customers = useCustomers();
  const { customerMarketAccountsList } = useCustomerMarketAccounts();

  const columns: ColumnDef<IFundingRateConfiguration>[] = useMemo(
    () => [
      {
        type: 'text',
        field: 'ConfigID',
        title: 'Config ID',
        initialWidth: 120,
        initialHide: true,
      },
      {
        type: 'text',
        field: 'Symbol',
        title: 'Symbol',
        initialWidth: 120,
      },
      {
        type: 'marketAccount',
        field: 'MarketAccount',
        title: 'Customer Account',
        initialWidth: 200,
        params: {
          prefixCustomerMarketAccountsWithCpty: true,
        },
      },
      {
        type: 'percent',
        field: 'LongPositionFundingRate',
        title: 'Long Position Rate',
        initialWidth: 150,
      },
      {
        type: 'percent',
        field: 'ShortPositionFundingRate',
        title: 'Short Position Rate',
        initialWidth: 150,
      },
      {
        type: 'text',
        field: 'CalculationMode',
        title: 'Calculation Mode',
        initialWidth: 150,
      },
      {
        type: 'text',
        field: 'RateDuration',
        title: 'Rate Duration',
        initialWidth: 120,
      },
    ],
    []
  );

  const drawerOptions: InputsAndDropdownsDrawerOption<IFundingRateConfiguration>[] = useMemo(() => {
    if (!customerMarketAccountsList || !customers) {
      return [];
    }

    const customersByName = new Map(customers.map(c => [c.Name, c]));

    return [
      {
        type: 'dropdown',
        field: 'MarketAccount',
        title: 'Market Account',
        showClear: true,
        placeholder: '*',
        getDropdownOptions: () =>
          customerMarketAccountsList.map(({ Name, SourceAccountID, Counterparty }) => {
            const customer = customersByName.get(Counterparty);
            const customerDisplayName = customer?.DisplayName ?? Counterparty;
            const accountID = SourceAccountID ?? Name;
            return {
              value: Name,
              label: `${customerDisplayName} - ${accountID}`,
            };
          }),
        disabledWhenEditing: true,
      },
      {
        type: 'symbolSelector',
        field: 'Symbol',
        title: 'Security',
        getIsRequired: () => true,
        disabledWhenEditing: true,
        disableKeyedOptions: true,
      },
      {
        type: 'inputNumeric',
        variant: 'percent',
        field: 'LongPositionFundingRate',
        title: 'Long Position Funding Rate',
        getIsRequired: () => true,
        placeholder: 'e.g., -0.05',
      },
      {
        type: 'inputNumeric',
        variant: 'percent',
        field: 'ShortPositionFundingRate',
        title: 'Short Position Funding Rate',
        getIsRequired: () => true,
        placeholder: 'e.g., 0.07',
      },
      {
        type: 'dropdown',
        field: 'CalculationMode',
        title: 'Calculation Mode',
        getIsRequired: () => true,
        getDropdownOptions: () => [
          { value: 'Absolute', label: 'Absolute' },
          { value: 'Relative', label: 'Relative' },
        ],
      },
      {
        type: 'input',
        field: 'RateDuration',
        title: 'Rate Duration',
        getIsRequired: () => true,
        placeholder: 'e.g., 360d',
      },
    ];
  }, [customerMarketAccountsList, customers]);

  const availableFilterableProperties = useMemo(() => {
    return [symbolFilter];
  }, [symbolFilter]);
  const { externallyControlledFilter, filterableProperties } = useSpaceParameterControlledFilter(
    CUSTOMER_FUNDING_RATE_CONFIGURATION_SPACE_PARAMETER_SUPPORT,
    availableFilterableProperties
  );

  return (
    <EntityAdmin<IFundingRateConfiguration>
      title="Funding Rate Configuration"
      subtitle="View and manage funding rate configurations."
      entityName="Funding Rate Configuration"
      path="/funding-rate-configurations"
      getPostPath={() => '/funding-rate-configurations'}
      getPatchPath={entity => `/funding-rate-configurations/${entity.ConfigID}`}
      getDeletePath={entity => `/funding-rate-configurations/${entity.ConfigID}`}
      entityIDField="Symbol"
      childIDField="MarketAccount"
      density={BlotterDensity.Comfortable}
      columns={columns}
      drawerOptions={drawerOptions}
      filterableProperties={filterableProperties}
      externallyControlledFilter={externallyControlledFilter}
      allowModeSwitch={false}
      allowAddEntity={isAuthorized(ACTION.EDIT_DEALER)}
      allowEditEntity={isAuthorized(ACTION.EDIT_DEALER)}
      allowDeleteEntity={isAuthorized(ACTION.EDIT_DEALER)}
      persistKey="funding-rate-configurations"
      blotterRowCount={DEFAULT_MAX_ROWS}
      paginationSize={DEFAULT_PAGINATION_SIZE}
      allowBulkEdit={false}
      wsRequest={WS_REQUEST}
    />
  );
};
